// axios interceptor for the JWT token (Auth0-first, storage-free)

import axios from 'axios'
import Vue from 'vue'
import store from '@/store/index.js'
import { shouldBypassAuth } from '@/helpers/authBypass'

function expandLocalOrigins(urlStr) {
  if (!urlStr) return []
  try {
    const u = new URL(urlStr)
    const list = [new URL(urlStr).origin]
    if (u.hostname === 'localhost') {
      const alt = new URL(urlStr)
      alt.hostname = '127.0.0.1'
      list.push(alt.origin)
    } else if (u.hostname === '127.0.0.1') {
      const alt = new URL(urlStr)
      alt.hostname = 'localhost'
      list.push(alt.origin)
    }
    return Array.from(new Set(list))
  } catch {
    return []
  }
}

// Configure which origins should receive Authorization headers
const ENV_API = process.env.VUE_APP_API_SERVER_URL
const DEV_FALLBACK_ORIGINS = ['http://localhost:8080', 'http://127.0.0.1:8080']
const ORIGINS = new Set(
  [
    ...expandLocalOrigins(ENV_API),
    ...DEV_FALLBACK_ORIGINS, // fallback for local dev when env var is missing/mismatched
    typeof window !== 'undefined' ? window.location.origin : null
  ].filter(Boolean)
)

function isApiRequest(url) {
  try {
    const asUrl = new URL(url, window.location.origin)
    const isBackendApi =
      asUrl.pathname.startsWith('/be/') || asUrl.pathname.includes('/be/api')
    if (isBackendApi) return true

    const match = Array.from(ORIGINS).some((o) => o === asUrl.origin)
    return match
  } catch {
    return false
  }
}

async function waitForAuth0Instance(maxMs = 2000) {
  const start = Date.now()
  while (!Vue.prototype?.$auth0 && Date.now() - start < maxMs) {
    await new Promise((r) => setTimeout(r, 50))
  }
  return Vue.prototype?.$auth0
}

async function waitForAuth0Ready(auth0, maxMs = 2000) {
  if (!auth0) return
  const start = Date.now()
  while (auth0.isLoading && Date.now() - start < maxMs) {
    await new Promise((r) => setTimeout(r, 50))
  }
}

async function getAuth0TokenSilently() {
  const auth0 = await waitForAuth0Instance()
  if (!auth0) return null

  try {
    // Wait for Auth0 to finish initializing to avoid token-less requests
    await waitForAuth0Ready(auth0)

    // If not authenticated, try to use cached tokens first
    if (!auth0.isAuthenticated) {
      try {
        // This will use cached tokens or attempt silent authentication
        const cachedToken = await auth0.getTokenSilently({
          ignoreCache: false,
          timeoutInSeconds: 5
        })
        if (cachedToken) {
          console.log('🎯 Retrieved cached/silent token successfully')
          return cachedToken
        }
      } catch (cacheError) {
        console.debug('No cached token available:', cacheError.message)
      }
    }

    // Always ask Auth0 SDK for a fresh/valid token; it refreshes under the hood
    return await auth0.getTokenSilently({
      timeoutInSeconds: 10,
      ignoreCache: false
    })
  } catch (e) {
    console.warn('Auth0 getTokenSilently failed:', e.message)
    // If token fails but we have stored user info, the user might still be logged in
    // but needs to re-authenticate
    const hasStoredUserInfo =
      localStorage.getItem('user_info') &&
      localStorage.getItem('USE_SSO') === 'true'
    if (hasStoredUserInfo && e.error !== 'login_required') {
      console.log(
        '🔄 Token failed but user info exists, may need re-authentication'
      )
    }
    return null
  }
}

export default function execute() {
  // Prevent multiple registrations in HMR/dev
  if (axios.__AUTH_INTERCEPTORS_INSTALLED__) return
  axios.__AUTH_INTERCEPTORS_INSTALLED__ = true

  // REQUEST: attach bearer to API calls
  axios.interceptors.request.use(
    async (config) => {
      if (shouldBypassAuth(config && config.url)) return config
      if (!isApiRequest(config.url)) return config

      const token = await getAuth0TokenSilently()
      if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
      } else {
        // minimal debug
        try {
          console.debug('[auth] no token attached for', config.url)
        } catch {}
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // RESPONSE: handle 401 once with a fresh token + retry
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (shouldBypassAuth(error?.config && error.config.url))
        return Promise.reject(error)

      const originalRequest = error?.config
      const status = error?.response?.status

      if (status === 401 && originalRequest && !originalRequest.__retry) {
        originalRequest.__retry = true
        try {
          const freshToken = await getAuth0TokenSilently()
          if (freshToken && isApiRequest(originalRequest.url)) {
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers.Authorization = `Bearer ${freshToken}`
            return axios(originalRequest)
          }
        } catch {}

        const auth0 = await waitForAuth0Instance()
        if (auth0) {
          try {
            await waitForAuth0Ready(auth0)
          } catch {}
        }
        const shouldMarkLoggedOut =
          Boolean(auth0) && auth0.isAuthenticated === false
        if (shouldMarkLoggedOut) {
          try {
            store.commit('auth/SSO_LOADED', undefined)
          } catch {}
          try {
            localStorage.setItem('user_logged_out', 'true')
          } catch {}
          try {
            localStorage.removeItem('authenticated')
          } catch {}
          const base = process.env.VUE_APP_SELFSUFFIX || ''
          const landingPath = `${base}/landing`.replace(/\/+/, '/')
          if (window.location.pathname !== landingPath) {
            window.location.replace(landingPath)
          }
        }
      }

      return Promise.reject(error)
    }
  )
}
